/**
 * Every screen the application has, in one place.
 *
 * A view is declared here and referenced by key everywhere else — the router,
 * the sidebar, the mobile tab bar and `ProtectedRoute` all read this table
 * rather than repeating a path string. The point is that a screen's path and
 * the conditions for reaching it are stated once.
 *
 * `staffOnly` is the only gate kdoshsop has. When the backend grows a role
 * model (kdoshsop-api/docs/adr/0001) this interface gains `permission` and
 * `anyRoles`, `canSeeView` starts consulting them, and every view already
 * declared here keeps working.
 */
import type { CurrentUser } from '@/api/types';

export interface ViewDef {
  path: string;
  /** i18n key. A view without one is reachable but never appears in navigation. */
  labelKey?: string;
  /** Requires `is_staff`. Today that means "can also reach the Django admin". */
  staffOnly?: boolean;
}

export const VIEWS = {
  home: { path: '/', labelKey: 'nav.home' },
  changePassword: { path: '/cambiar-contrasena' },
} satisfies Record<string, ViewDef>;

export type ViewKey = keyof typeof VIEWS;

/** Views that can appear in navigation, i.e. the ones with a label. */
export type NavigableViewKey = {
  [K in ViewKey]: (typeof VIEWS)[K] extends { labelKey: string } ? K : never;
}[ViewKey];

/**
 * UI convenience only. The API remains the authority for every operation —
 * hiding a control is a courtesy, not a control.
 */
export function canSeeView(user: CurrentUser | null, view: ViewDef): boolean {
  if (!user) return false;
  if (view.staffOnly && !user.is_staff) return false;
  return true;
}
